import type { MouseEvent, JSX } from 'react';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import * as yup from 'yup';
import type { NoOpFn } from '~/utilities/util';
import type { FormState } from '@wistia/vhs';
import {
  Checkbox,
  datetimeHelpers,
  Divider,
  Form,
  FormButtons,
  FormField,
  useOnClickOutside,
  useThemeColor,
} from '@wistia/vhs';
import { styled } from 'styled-components';
import type { WistiaPlayer } from '@wistia/wistia-player';
import { useLegacyWistiaPlayer } from '~/hooks/player/useLegacyWistiaPlayer';
import { useWistiaPlayer } from '~/hooks/player/useWistiaPlayer';
import type { Nullable } from '~/utilities/type-guards';
import { isNotNil } from '~/utilities/type-guards';
import {
  useCurrentPlayerTimeLegacy,
  useCurrentPlayerTime,
} from '~/hooks/player/useCurrentPlayerTime';
import { trackStructEvent } from '~/utilities/snowplow';
import { CommentingDisabledTooltip } from '~/singlePageApp/screens/AnonymousMedia/layouts/Shared';
import type { Ev1Player } from 'types/e-v1-player-api-types';
import { CommentHeading } from '../CommentHeading';
import type { ContactData } from '../types';
import { commentingViews, useCommentContext } from '../CommentContext';
import { BodyFieldWithContactSelect } from './BodyFieldWithContactSelect';
import { CommentAvatar } from '../CommentAvatar';
import { STICKY_TAB_BAR_WRAPPER_HEIGHT } from '../../MediaLayoutComponents';
import { getStickyOptionsWrapperHeight } from '../CommentTabs';
import { MediaStoreSelectors, useMediaStore } from '../../Store/MediaStore';

const { mediaDurationString } = datetimeHelpers;

const formSchema = yup.object().shape({
  body: yup.string().trim().required(),
});

const StickyForm = styled(Form)<{
  $isTopLevel: boolean;
  $isRightOfPlayer: boolean;
  $isReply?: boolean;
}>`
  margin: 0 0 16px;
  padding-bottom: 16px;
  padding-top: ${({ $isReply }) => ($isReply ? '0px' : '20px')};
  position: ${({ $isTopLevel }) => ($isTopLevel ? 'sticky' : '')};
  width: 100%;

  ${({ $isTopLevel }) => !$isTopLevel && `background: transparent;`}
`;

const FormWrapper = styled(CommentHeading)`
  align-items: center;
`;

const SubmitWrapper = styled.div`
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing.space02};
  display: flex;
  justify-content: space-between;
`;

const SubmitContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 4px;
  justify-content: flex-end;
`;

const CommentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  & fieldset {
    margin: 0;
  }
`;

const StyledCheckbox = styled(Checkbox)`
  width: auto;
`;

const StyledInput = styled(FormField)`
  /* stylelint-disable-next-line plugin/use-baseline */
  field-sizing: content;
`;

const ReplyFormWrapper = styled.div`
  & fieldset {
    margin-bottom: 0;
  }
`;

const ReplyFieldWrapper = styled.div`
  column-gap: ${({ theme }) => theme.spacing.space02};
  display: flex;
  margin-bottom: 8px;
`;

const StyledDivider = styled(Divider)`
  border-radius: 2px;
`;

const BodyFormField = memo(
  ({
    isFocused,
    setIsFocused,
    contact,
    isReply,
    fieldRef,
    player,
  }: {
    isFocused: boolean;
    setIsFocused: React.Dispatch<React.SetStateAction<boolean>>;
    contact: ContactData | null | undefined;
    isReply: boolean;
    fieldRef: React.RefObject<HTMLInputElement | null>;
    player: Ev1Player | WistiaPlayer | null;
  }) => {
    const { commentingView } = useCommentContext();
    const type = 'multiline';
    const label = isReply ? '' : `Commenting as ${contact?.name ?? 'Unavailable'}`;
    const placeholder = `Leave a ${isReply ? 'reply' : 'comment'}`;

    const onFocus = (): void => {
      setIsFocused(true);
      void player?.pause();
    };

    if (commentingView === commentingViews.audience) {
      return (
        <FormWrapper>
          <StyledInput
            name="body"
            placeholder={placeholder}
            label={label}
            labelProps={{ screenReaderOnly: true }}
            type={type}
            onFocus={onFocus}
            autoFocus={isFocused}
            onClick={(event) => event.preventDefault()}
          />
        </FormWrapper>
      );
    }
    return (
      <FormWrapper>
        <BodyFieldWithContactSelect
          fieldRef={fieldRef}
          placeholder={placeholder}
          onFocus={onFocus}
          autoFocus={isFocused}
        />
      </FormWrapper>
    );
  },
);

const ReplyFormFields = ({
  contact,
  hideForm,
  isFocused,
  setIsFocused,
  isReply,
  formRef,
}: {
  contact: ContactData | null | undefined;
  hideForm?: NoOpFn;
  isFocused: boolean;
  setIsFocused: React.Dispatch<React.SetStateAction<boolean>>;
  isReply: boolean;
  formRef: React.RefObject<FormState | null>;
}): JSX.Element => {
  const inputFieldRef = useRef<HTMLInputElement>(null);
  return (
    <ReplyFormWrapper>
      <ReplyFieldWrapper>
        <CommentAvatar contact={contact} />
        <BodyFormField
          fieldRef={inputFieldRef}
          isFocused={isFocused}
          setIsFocused={setIsFocused}
          contact={contact}
          isReply={isReply}
          player={null}
        />
      </ReplyFieldWrapper>
      <FormButtons
        align="right"
        buttons={[
          {
            type: 'button',
            variant: 'tertiary',
            size: 'md',
            name: 'Cancel',
            onClick: (event: MouseEvent<HTMLButtonElement>) => {
              event.preventDefault();
              hideForm?.();
            },
          },
          {
            type: 'submit',
            size: 'md',
            name: 'Reply',
            variant: 'primary',
            onClick: (event: MouseEvent<HTMLButtonElement>) => {
              event.preventDefault();
              formRef.current?.handleSubmit();
            },
          },
        ]}
      />
    </ReplyFormWrapper>
  );
};

const getPlayerHook = (shouldUseLegacyPlayerApi: boolean) => {
  if (shouldUseLegacyPlayerApi) {
    return useLegacyWistiaPlayer;
  }

  return useWistiaPlayer;
};

const getCurrentPlayerTimeHook = (
  shouldUseLegacyPlayerApi: boolean,
): ((player: Ev1Player | WistiaPlayer | null) => number) => {
  if (shouldUseLegacyPlayerApi) {
    return useCurrentPlayerTimeLegacy;
  }

  return useCurrentPlayerTime;
};

export const CreateCommentForm = ({
  mediaId = '',
  contact,
  isReply,
  isRightOfPlayer = false,
  parentId = null,
  hideForm,
  commentingDisabled = false,
}: {
  mediaId: string;
  contact: ContactData | null | undefined;
  isReply: boolean;
  isRightOfPlayer: boolean;
  parentId?: string | null;
  hideForm?: NoOpFn;
  commentingDisabled?: boolean;
}): JSX.Element => {
  const formGlobalErrorRef = useRef(undefined);
  const focusedElementRef = useRef<Nullable<HTMLDivElement>>(null);
  const inputFieldRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<FormState>(null);

  const {
    createComment,
    isAnonymousMedia,
    commentingView,
    shouldUseLegacyPlayerApi,
    showCommentTabs,
  } = useCommentContext();
  const stickyPlayerPhantomHeight = useMediaStore(MediaStoreSelectors.getStickyPlayerPhantomHeight);
  const playerStickyPosition = useMediaStore(MediaStoreSelectors.getPlayerStickyPosition);
  const stickyOptionsWrapperHeight = getStickyOptionsWrapperHeight(
    commentingView,
    showCommentTabs,
    playerStickyPosition === 'top',
  );

  const playerHook = getPlayerHook(shouldUseLegacyPlayerApi);
  const player = playerHook();
  const grey100 = useThemeColor('grey100');

  const [addTimecode, setAddTimecode] = useState(commentingView === commentingViews.team);
  const [isFocused, setIsFocused] = useState(false);

  const timeHook = getCurrentPlayerTimeHook(shouldUseLegacyPlayerApi);
  const currentPlayerTime = timeHook(player);

  const showDivider = isRightOfPlayer && !isAnonymousMedia;

  const stickyFormTopPx = useMemo<number>(() => {
    if (!isReply && isRightOfPlayer) {
      return 0;
    }
    if (playerStickyPosition === 'top') {
      return Number(
        stickyPlayerPhantomHeight + STICKY_TAB_BAR_WRAPPER_HEIGHT + stickyOptionsWrapperHeight,
      );
    }
    return STICKY_TAB_BAR_WRAPPER_HEIGHT + stickyOptionsWrapperHeight;
  }, [
    isReply,
    isRightOfPlayer,
    playerStickyPosition,
    stickyPlayerPhantomHeight,
    stickyOptionsWrapperHeight,
  ]);

  useEffect(() => {
    if (isReply) {
      setIsFocused(true);
    }
  }, [isReply]);

  useEffect(() => {
    if (commentingView === commentingViews.team) {
      setAddTimecode(true);
    } else {
      setAddTimecode(false);
    }
  }, [commentingView]);

  const onSubmitForm = (
    values: { body: string },
    {
      setSubmitting,
      resetForm,
    }: {
      setSubmitting: (isSubmitting: boolean) => void;
      resetForm: () => void;
    },
  ) => {
    const { body } = values;
    let mediaTimestamp: number | null = null;
    const embed = player as Ev1Player;
    if (isNotNil(player) && !isReply && addTimecode) {
      if (typeof embed.time === 'function') {
        mediaTimestamp = embed.time();
      } else {
        mediaTimestamp = (player as WistiaPlayer).currentTime;
      }
    }

    hideForm?.();
    setSubmitting(true);
    createComment(body, parentId, mediaTimestamp, mediaId);
    setSubmitting(false);
    resetForm();

    if (commentingView === commentingViews.team) {
      setAddTimecode(true);
    } else {
      setAddTimecode(false);
    }

    if (!isAnonymousMedia) {
      trackStructEvent({
        category: 'button',
        action: 'click',
        label: `leave-media-page-${commentingView.toLowerCase()}-${isReply ? 'reply' : 'comment'}`,
      });
    }
  };

  useOnClickOutside(focusedElementRef, () => setIsFocused(false));

  return (
    <>
      <StickyForm
        formId="createCommentForm"
        globalErrorRef={formGlobalErrorRef}
        initialValues={{
          body: '',
        }}
        innerRef={formRef}
        onSubmit={onSubmitForm}
        validationSchema={formSchema}
        $isTopLevel={!isReply}
        $isRightOfPlayer={isRightOfPlayer}
        $isReply={isReply}
        style={{
          top: `${stickyFormTopPx}px`,
        }}
      >
        {isReply ? (
          <div ref={focusedElementRef}>
            <ReplyFormFields
              contact={contact}
              hideForm={hideForm}
              isFocused={isFocused}
              setIsFocused={setIsFocused}
              isReply={isReply}
              formRef={formRef}
            />
          </div>
        ) : (
          <CommentWrapper ref={focusedElementRef}>
            <BodyFormField
              contact={contact}
              isFocused={isFocused}
              setIsFocused={setIsFocused}
              isReply={isReply}
              fieldRef={inputFieldRef}
              player={player}
            />
            <SubmitWrapper>
              <CommentAvatar contact={contact} />
              <SubmitContainer>
                <StyledCheckbox
                  checked={addTimecode}
                  label={mediaDurationString(currentPlayerTime)}
                  onChange={() => setAddTimecode(!addTimecode)}
                />
                <CommentingDisabledTooltip disabled={commentingDisabled}>
                  <FormButtons
                    align="left"
                    buttons={[
                      {
                        type: 'submit',
                        name: 'Comment',
                        variant: 'primary',
                        size: 'md',
                        disabled: commentingDisabled,
                      },
                    ]}
                  />
                </CommentingDisabledTooltip>
              </SubmitContainer>
            </SubmitWrapper>
          </CommentWrapper>
        )}
      </StickyForm>
      {showDivider ? (
        <StyledDivider
          variant="solid"
          height={4}
          colorOverride={grey100}
        />
      ) : null}
    </>
  );
};
